import { currentPostedDate, getErrorMessage } from '@fruitful/fruitils';
import { zodResolver } from '@hookform/resolvers/zod';
import { router, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BackButton,
    BodyLargeEmphasized,
    BodySmallEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledInput,
    Form,
    H2Regular,
    isWeb,
    SafeAreaView,
    ScrollView,
    useToastController,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { assertNotNullish, getRandomNumber } from '../../../common/misc';
import { DEFAULT_DATE_FORMAT, format } from '../../../common/utils/dateUtils';
import { captureException, captureMessage } from '../../../logger/Logger';
import useCreateIncomingAchSimulation, {
    type AchTransactionSimulationRequest,
    type AchTransactionSimulationRequestDcSignEnum,
    type IncomingAchSimulationResponse,
} from '../../hooks/transaction/useCreateIncomingAchSimulation';
import useGetAllBankAccountDetails from '../../hooks/useGetAllBankAccountDetails';

const incomingAchSimulationSchema = z.object({
    accountNumber: z.string(),
    amount: z.string().min(1, 'Please enter an amount'),
    dcSign: z.enum(['credit', 'debit']),
    effectiveDate: z.string(),
});

type IncomingAchSimulationSchema = z.infer<typeof incomingAchSimulationSchema>;

interface SimulateIncomingAchProps {
    accountNumber: string;
    submit: (data: AchTransactionSimulationRequest) => void;
    isSubmitting: boolean;
}

const SimulateIncomingAchForm = ({
    accountNumber,
    submit,
    isSubmitting,
}: SimulateIncomingAchProps) => {
    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<IncomingAchSimulationSchema>({
        resolver: zodResolver(incomingAchSimulationSchema),
        defaultValues: {
            accountNumber,
            amount: getRandomNumber(1, 10000).toString(),
            dcSign: 'credit',
            effectiveDate: format(currentPostedDate(), DEFAULT_DATE_FORMAT),
        },
    });

    const onSubmit: SubmitHandler<IncomingAchSimulationSchema> = ({
        accountNumber,
        amount,
        dcSign,
        effectiveDate,
    }) => {
        const data: AchTransactionSimulationRequest = {
            account_number: assertNotNullish(accountNumber),
            amount: parseInt(amount ?? '', 10),
            dc_sign: dcSign as AchTransactionSimulationRequestDcSignEnum,
            effective_date: assertNotNullish(effectiveDate),
        };
        submit(data);
    };

    return (
        <ScrollView>
            <Form onSubmit={handleSubmit(onSubmit)} flex={1}>
                <YStack gap={12} flex={1} minWidth={500}>
                    <ControlledInput
                        name="accountNumber"
                        label="Account Number"
                        control={control}
                        register={register}
                        errors={errors}
                        editable={false}
                        disabled
                    />
                    <ControlledInput
                        name="amount"
                        label="Amount (cents)"
                        control={control}
                        register={register}
                        errors={errors}
                        type="numeric"
                        inputMode="numeric"
                    />
                    <ControlledInput
                        name="dcSign"
                        label="credit or debit"
                        control={control}
                        register={register}
                        errors={errors}
                        editable={true}
                    />
                    <ControlledInput
                        name="effectiveDate"
                        label="Effective Date"
                        control={control}
                        register={register}
                        errors={errors}
                        editable={true}
                    />

                    <Button
                        tone="positive"
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Simulate incoming ACH"
                        onPress={handleSubmit(onSubmit)}
                        disabled={!isValid || isSubmitting}
                    />
                </YStack>
            </Form>
        </ScrollView>
    );
};

const SimulateIncomingAch = () => {
    const { accountNumber } = useLocalSearchParams<{
        accountNumber: string;
    }>();

    const toast = useToastController();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [incomingAchResponse, setIncomingAchResponse] =
        useState<IncomingAchSimulationResponse | null>(null);

    const { mutateAsync: createIncomingAchSimulation } =
        useCreateIncomingAchSimulation();
    const { refetchAccountDetails } = useGetAllBankAccountDetails(false);

    const submit = async (body: AchTransactionSimulationRequest) => {
        setIsSubmitting(true);
        setIncomingAchResponse(null);

        toast.show('Waiting for incoming transaction to post...', {
            duration: 3000,
        });

        try {
            const incomingAchSimulationResponse =
                await createIncomingAchSimulation({
                    body,
                });

            toast.show('Created incoming ACH transaction', {
                duration: 2000,
            });
            captureMessage(
                `Created incoming ACH transaction: ${JSON.stringify(incomingAchSimulationResponse)}`,
            );
            if (incomingAchSimulationResponse.success) {
                setIncomingAchResponse(incomingAchSimulationResponse);

                // Refresh account details (balance) and transactions table
                void refetchAccountDetails();
                // TODO uncomment below after larger refactor related to how we setup query keys is fixed (separate PR)
                // await queryClient.invalidateQueries({
                //     queryKey: ['getTransactionTable'],
                // });
            }
        } catch (e) {
            captureException(e);
            toast.show(`Error! ${getErrorMessage(e)}`, {
                duration: 2000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (accountNumber == null) {
        captureException(
            new Error(
                'Missing accountNumber query param for incoming ACH simulation',
            ),
        );
        router.back();
    }

    return (
        <SafeAreaView>
            <ScrollView showsVerticalScrollIndicator={isWeb}>
                <YStack
                    maxWidth={700}
                    alignSelf="center"
                    marginTop={isWeb ? 24 : 0}
                    marginHorizontal={16}
                >
                    <BodyLargeEmphasized alignSelf="center">
                        Simulate Incoming ACH
                    </BodyLargeEmphasized>
                    <View marginTop={-26} marginBottom={24}>
                        <BackButton />
                    </View>

                    <View marginBottom={16}>
                        <SimulateIncomingAchForm
                            submit={submit}
                            isSubmitting={isSubmitting}
                            accountNumber={accountNumber}
                        />
                    </View>
                    {incomingAchResponse != null && (
                        <YStack>
                            <H2Regular>
                                Simulated normalized transaction response:
                            </H2Regular>
                            <YStack style={{ whiteSpace: 'pre' }}>
                                <BodySmallRegular>
                                    {JSON.stringify(
                                        incomingAchResponse.normalizedTransaction,
                                        null,
                                        2,
                                    )}
                                </BodySmallRegular>
                            </YStack>
                        </YStack>
                    )}
                    <YStack>
                        <H2Regular>Instructions</H2Regular>
                        <BodySmallRegular>
                            See
                            TransactionNormalizationService2.createRdfiTestingScenarios
                            <View width="100%" height={28} />
                            Transactions ending in 0 come from{' '}
                            <BodySmallEmphasized>
                                Sequoia One PEO,
                            </BodySmallEmphasized>{' '}
                            with ACH description{' '}
                            <BodySmallEmphasized>
                                EDI PYMNTS
                            </BodySmallEmphasized>
                            <View width="100%" height={14} />
                            Transactions ending in 1 come from{' '}
                            <BodySmallEmphasized>
                                APPLE
                            </BodySmallEmphasized>{' '}
                            with ACH description{' '}
                            <BodySmallEmphasized>PAYROLL</BodySmallEmphasized>
                            <View width="100%" height={14} />
                            Transactions ending in 2 come from{' '}
                            <BodySmallEmphasized>
                                VENMO
                            </BodySmallEmphasized>{' '}
                            with ACH description{' '}
                            <BodySmallEmphasized>CASHOUT</BodySmallEmphasized>.
                            This is not considered a paycheck / eligible for
                            cash back rewards.
                        </BodySmallRegular>
                    </YStack>
                </YStack>
            </ScrollView>
        </SafeAreaView>
    );
};

export default SimulateIncomingAch;
